import { AHIcon, AHText, Box, TouchableBox, useLayoutContext } from "arkh";
import React, { PropsWithChildren } from "react";
import { graphql, useFragment } from "react-relay";
import {
  FileIcon,
  FileIconRelay,
  FileIconType,
} from "../features/Files/FileListItem/FileIcon";
import {
  FileCollectionItem_File$data,
  FileCollectionItem_File$key,
} from "./__generated__/FileCollectionItem_File.graphql";

export type FileItemType = {
  id: string;
  url: string;
  type: FileIconType | null;
  name: string;
};

export const FileCollection = (props: PropsWithChildren<object>) => {
  const layout = useLayoutContext();
  return (
    <Box
      spaceHorizontal="medium"
      spaceBottom="small"
      style={{
        flexWrap: "wrap",
        flexDirection: "row",
        alignItems: "center",
        marginRight: -layout.space.xsmall,
        marginBottom: -layout.space.xsmall,
      }}
    >
      {props.children}
    </Box>
  );
};

const FileCollectionItemContainer = (
  props: PropsWithChildren<{ onPress?(): void; onRemove?(): void }>
) => {
  const layout = useLayoutContext();

  return (
    <Box
      spaceHorizontal="small"
      spaceVertical="small"
      cornerRadius="large"
      borderColor="border-primary"
      style={{
        borderWidth: 1,
        flexDirection: "row",
        alignItems: "center",
        marginBottom: layout.space.xsmall,
        marginRight: layout.space.xsmall,
      }}
    >
      <TouchableBox
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
        disabled={!props.onPress}
        onPress={props.onPress}
      >
        {props.children}
      </TouchableBox>
      {props.onRemove ? (
        <>
          <Box spaceLeft="small" />
          <Box
            style={{ alignSelf: "stretch", width: 1 }}
            color="border-primary"
          />
          <Box spaceLeft="xsmall" />
          <TouchableBox
            onPress={props.onRemove}
            pressRetentionOffset={{
              left: 20,
              right: 20,
              top: 20,
              bottom: 20,
            }}
            hitSlop={{ left: 20, right: 20, top: 20, bottom: 20 }}
          >
            <AHIcon type="minus" color="negative" size="small" />
          </TouchableBox>
        </>
      ) : null}
    </Box>
  );
};

export const FileCollectionItemRelay = (props: {
  file: FileCollectionItem_File$key;
  onShowFile(_: FileCollectionItem_File$data): void;
  onRemove?(): void;
  hideOrg?: boolean;
}) => {
  const file = useFragment(
    graphql`
      fragment FileCollectionItem_File on File {
        name
        id
        url
        type
        ...FileIcon_File
      }
    `,
    props.file
  );

  return (
    <FileCollectionItemContainer
      onPress={() => {
        props.onShowFile(file);
      }}
      onRemove={props.onRemove}
    >
      <FileIconRelay file={file} size={17} hideOrg={props.hideOrg} />
      <AHText type="subtitle" spaceLeft="xxsmall">
        {file.name}
      </AHText>
    </FileCollectionItemContainer>
  );
};

export const FileCollectionItem = (props: {
  file: FileItemType;
  onShowFile?(_: FileItemType): void;
  onRemove?(): void;
}) => {
  return (
    <FileCollectionItemContainer
      onPress={
        props.onShowFile
          ? () => {
              props.onShowFile?.(props.file);
            }
          : undefined
      }
      onRemove={props.onRemove}
    >
      <FileIcon filetype={props.file.type} size={17} />
      <AHText type="subtitle" spaceLeft="xxsmall">
        {props.file.name}
      </AHText>
    </FileCollectionItemContainer>
  );
};
